// Retailer portal — first-run BILLING SETUP prompt (subscription-first onboarding).
//
// Replaces the old mandatory "Connect with Stripe" full-portal wall. Under
// subscription billing ([[project_subscription_billing_build]]) TKOS revenue
// comes from the GoCardless Direct Debit, NOT Stripe — so platform access is no
// longer gated on Stripe. Instead, on first login the ACCOUNT OWNER is shown a
// one-time prompt to set up their Direct Debit mandate, with a "Skip for now"
// escape. After skipping they get full portal access plus a persistent
// (non-blocking) reminder banner (rendered by the shell).
//
// This prompt only makes sure billing setup is SEEN — it NEVER blocks access.
// Real enforcement of a lapsed subscription lives in the trial/dunning/
// suspension system (`requireActiveSubscription`, server-side). See
// /root/brief_stripe_gate_rethink.md (Option A, "prompt once then nudge").
//
// READ-ONLY w.r.t. money code: reads GET /api/subscription/status and starts
// POST /api/subscription/mandate/start. Touches NO commission / application-fee
// / transfer / cap / webhook logic.

import { api, ApiError } from "@tkos/api-client";
import { logout } from "./auth";

// UI-pref flag only (see [[feedback-localstorage-rule]]): "the owner has seen
// and dismissed the first-run billing prompt". Not business data.
const DISMISS_KEY = "kos_billing_prompt_dismissed";

interface SubscriptionView {
  billingStatus: string;
  mandateConnected: boolean;
}
interface StatusResponse {
  ok: boolean;
  gocardlessConfigured: boolean;
  subscription: SubscriptionView | null;
}
interface MandateStartResponse {
  ok: boolean;
  authorisationUrl?: string;
  error?: string;
}

/**
 * True when the account owner still needs to set up their Direct Debit mandate.
 *
 * Fail-CLOSED to `false` — if we can't tell (endpoint error, or billing isn't
 * configured in this environment), we never nag. Access is already open, so the
 * safe default is "don't prompt". Only the two pre-mandate states
 * (`none` / `pending_mandate`) return true; every other billing_status
 * (trialing/active/past_due/suspended/exempt/cancelled) is owned by the
 * billing + dunning system, not this first-run prompt.
 */
export async function needsBillingSetup(): Promise<boolean> {
  try {
    const s = await api<StatusResponse>("/subscription/status");
    if (!s || !s.gocardlessConfigured) return false;
    const sub = s.subscription;
    if (!sub) return true; // no subscription row yet → mandate not set up
    if (sub.mandateConnected) return false;
    return sub.billingStatus === "pending_mandate" || sub.billingStatus === "none";
  } catch {
    return false;
  }
}

/** Has the owner already dismissed ("Skip for now") the first-run prompt? */
export function billingPromptDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}
/** Remember that the owner skipped the prompt (they still get the reminder). */
export function dismissBillingPrompt(): void {
  try {
    localStorage.setItem(DISMISS_KEY, "1");
  } catch {
    /* private-mode / storage-disabled: harmless, they'll just be prompted again */
  }
}

/**
 * Render the first-run billing-setup screen. Unlike the old Stripe wall this is
 * NOT a hard lock — "Skip for now" grants full access. `onRecheck` re-runs the
 * gate flow (after the owner says they've set it up); `onSkip` after they skip;
 * `onLoggedOut` after they sign out.
 */
export function renderBillingGate(
  rootEl: HTMLElement,
  handlers: { onRecheck: () => void; onSkip: () => void; onLoggedOut: () => void },
): void {
  rootEl.replaceChildren();

  const wrap = document.createElement("div");
  wrap.className = "tkos-billing-gate";
  wrap.innerHTML = `
    <div class="tkos-billing-gate__bg" aria-hidden="true"></div>
    <div class="tkos-billing-gate__card" role="dialog" aria-labelledby="billing-gate-title">
      <div class="tkos-billing-gate__eyebrow">Kitchengine</div>

      <div class="tkos-billing-gate__badge" aria-hidden="true">
        <span class="tkos-billing-gate__badge-glow"></span>
        <svg viewBox="0 0 24 24" width="30" height="30" fill="none"
             stroke="currentColor" stroke-width="1.5"
             stroke-linecap="round" stroke-linejoin="round">
          <rect x="3" y="6" width="18" height="13" rx="2.5"/>
          <path d="M3 10.5h18"/>
          <path d="M6.5 15.5h4"/>
        </svg>
      </div>

      <h1 id="billing-gate-title" class="tkos-billing-gate__title">Set up billing to get started</h1>
      <p class="tkos-billing-gate__sub">
        Add a Direct Debit for your monthly subscription. This starts your free trial — you
        won't be charged today, and you can always set it up later from Settings.
      </p>

      <button class="tkos-billing-gate__cta" type="button" data-role="setup">
        <span>Set up Direct Debit</span>
      </button>
      <div class="tkos-billing-gate__error" data-role="error"></div>

      <button type="button" class="tkos-billing-gate__skip" data-role="skip">Skip for now</button>

      <p class="tkos-billing-gate__fine">Takes 2 minutes — you'll need your business bank details. Bank-level Direct Debit, secured by GoCardless.</p>

      <div class="tkos-billing-gate__foot">
        <button type="button" class="tkos-billing-gate__link" data-role="recheck">I've already set it up — refresh</button>
        <span aria-hidden="true">·</span>
        <button type="button" class="tkos-billing-gate__link" data-role="signout">Sign out</button>
      </div>
    </div>
  `;
  rootEl.appendChild(wrap);

  const setupBtn = wrap.querySelector<HTMLButtonElement>("[data-role='setup']")!;
  const setupLabel = setupBtn.querySelector<HTMLSpanElement>("span")!;
  const skipBtn = wrap.querySelector<HTMLButtonElement>("[data-role='skip']")!;
  const recheckBtn = wrap.querySelector<HTMLButtonElement>("[data-role='recheck']")!;
  const signoutBtn = wrap.querySelector<HTMLButtonElement>("[data-role='signout']")!;
  const errEl = wrap.querySelector<HTMLDivElement>("[data-role='error']")!;

  setupBtn.addEventListener("click", async () => {
    setupBtn.disabled = true;
    setupBtn.classList.add("is-loading");
    setupLabel.textContent = "Opening…";
    errEl.textContent = "";
    try {
      const data = await api<MandateStartResponse>("/subscription/mandate/start", {
        method: "POST",
        body: JSON.stringify({}),
      });
      if (data.authorisationUrl) {
        // GoCardless hosted flow needs a full-page navigation.
        window.location.assign(data.authorisationUrl);
        return;
      }
      errEl.textContent = data.error || "Billing setup isn't available yet.";
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        errEl.textContent = "Billing isn't configured for this environment yet.";
      } else {
        errEl.textContent =
          "Couldn't start billing setup. Please try again, or set it up in Settings → Billing.";
      }
    } finally {
      setupBtn.disabled = false;
      setupBtn.classList.remove("is-loading");
      setupLabel.textContent = "Set up Direct Debit";
    }
  });

  skipBtn.addEventListener("click", () => {
    dismissBillingPrompt();
    handlers.onSkip();
  });

  recheckBtn.addEventListener("click", () => handlers.onRecheck());

  signoutBtn.addEventListener("click", async () => {
    try {
      await logout();
    } finally {
      handlers.onLoggedOut();
    }
  });
}
